import { FormProvider, useForm, Resolver } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useModal } from "src/hooks/useModal";
import { formatPhoneNumber } from "src/utils/common";
import { getProductFromSessionStorage } from "src/utils/storage/auth";
import { Dispatch, SetStateAction, useEffect, useMemo } from "react";
import RightDetailBox from "../RightDetailBox/RightDetailBox";
import { notifySuccess } from "src/components/Common/Toast";
import { useRouter } from "src/hooks/useRouter";
import styles from "./AskPromotion.module.scss";
import { FormContainer } from "components/Common/Form/Input/FormContainer";
import FormInput from "components/Common/Form/Input/FormInput";
import FormDate from "components/Common/Form/FormDate";
import Checkbox from "components/Common/Form/Checkbox";
import useCreateServicePromotionAsk from "src/hooks/api/pay/useCreateServicePromotionAsk";
import { askPromotionSchema, FormValuesByProduct } from "./AskPromotion.schema";
import { useIsDspProduct } from "src/hooks/recoil/useIsDspProduct";
import { useIsWrapsodyEcoProduct } from "src/hooks/recoil/useIsWrapsodyEcoProduct";
import { userInfoAtom } from "src/atoms";
import { useRecoilState } from "recoil";
import { useIsDspmProduct } from "src/hooks/recoil/useIsDspmProduct";
import { useTranslation } from "react-i18next";
import { useIsWrapsodyProduct } from "src/hooks/recoil/useIsWrapsodyProduct";
import { useIsFiresideProduct } from "src/hooks/recoil/useFiresideProduct";
import SelectDspmInfoList from "./SelectDspmInfoList";
import SelectDspmApplicationInfoList from "./SelectDspmApplicationInfoList";
// import { IssueAppType } from "src/service/types/pay.type";

type IssueAppType = {
  appName: string;
  appVersion: string;
};

// 정확한 FormValues 타입 정의
type FormValues = {
  companyName: string;
  startDate: string;
  endDate: string;
  externalLicenseCount?: number;
  licenseCount: number;
  product: string;
  purpose: string;
  telNumber: string;
  userName: string;
  userPerStorage?: number;
  issueAppList?: IssueAppType[];
  confirmPolicy: boolean;
  packageName?: string;
};

interface IAskPromotionProps {
  setToggleAskPromotionOn: Dispatch<SetStateAction<boolean>>;
}

const AskPromotion = ({ setToggleAskPromotionOn }: IAskPromotionProps) => {
  const product = getProductFromSessionStorage();
  const { schema, defaultValues: rawDefaultValues } = useMemo(
    () => askPromotionSchema(product),
    [product]
  );
  type SchemaFormValues = yup.InferType<typeof schema>;
  const route = useRouter();
  const { isDspProduct } = useIsDspProduct();
  const { isWrapsody } = useIsWrapsodyProduct();
  const { isWrapsodyEco } = useIsWrapsodyEcoProduct();
  const { isDspmProduct } = useIsDspmProduct();
  const { isFiresideProduct } = useIsFiresideProduct();
  const [userInfo] = useRecoilState(userInfoAtom);
  // 제품에 따른 정확한 타입 추론
  type CurrentFormValues = FormValuesByProduct<typeof product>;
  const defaultValues = useMemo(
    () => ({
      ...rawDefaultValues,
      userName: userInfo?.userName ?? "",
      companyName: userInfo?.companyName ?? "",
    }),
    [rawDefaultValues, userInfo?.userName, userInfo?.companyName]
  );
  const methods = useForm<CurrentFormValues>({
    defaultValues,
    resolver: yupResolver(schema) as Resolver<CurrentFormValues>,
    mode: "onChange",
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    formState: { isValid, errors },
  } = methods;
  const { t } = useTranslation();

  const { promotionAsk, isPromotionAskLoading } = useCreateServicePromotionAsk({
    onSuccess: () => {
      notifySuccess(t("siteType.promotionApply.title"));
      setToggleAskPromotionOn((prev) => !prev);
      route.goSitePromotion({});
    },
  });

  const onSubmit = (data: SchemaFormValues) => {
    const reqData: any = {
      product: data?.product,
      userName: data.userName,
      telNumber: formatPhoneNumber(data.telNumber),
      companyName: data.companyName,
      purpose: data?.purpose ?? "",
      licenseCount: data?.licenseCount ?? 0,
      userPerStorage: data?.userPerStorage ?? 0,
      startDate: data?.startDate ?? "",
      endDate: data?.endDate ?? "",
      issueAppList: data?.issueAppList ?? [],
    };
    // 조건부 필드 추가 (타입 단언 사용)
    // if (isDspProduct) {
    //   reqData.packageName = (data as any).packageName ?? "";
    // }
    // if (isWrapsodyEco) {
    //   reqData.externalLicenseCount = (data as any).externalLicenseCount ?? 0;
    // }
    const finalData = {
      ...reqData,
      // ...(isDspProduct && "packageName" in data
      //   ? { packageName: data.packageName }
      //   : {}),
      ...(isWrapsodyEco && "externalLicenseCount" in data
        ? { externalLicenseCount: data.externalLicenseCount }
        : {}),
      ...(isDspmProduct && "dspmStorageInfo" in data
        ? { dspmStorageInfoList: data.dspmStorageInfo }
        : {}),
      ...(isDspmProduct && "dspmApplicationInfo" in data
        ? { dspmApplicationInfoList: data.dspmApplicationInfo }
        : {}),
      ...(isDspProduct && "requestProducts" in data
        ? { requestProducts: data.requestProducts }
        : {}),
    };
    promotionAsk(finalData);
    // console.log("promotionAsk", finalData, data);
  };

  const { openTermsService, openPrivatePolicy, openPaidTermsService } =
    useModal();

  // telNumber 값 감지하여 포맷 적용
  useEffect(() => {
    const subscription = watch(
      (value: any, { name, type }: { name?: string; type?: string }) => {
        if (name === "telNumber" && value.telNumber) {
          const formatted = formatPhoneNumber(value.telNumber);
          if (value.telNumber !== formatted) {
            (setValue as any)("telNumber", formatted, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }
        }
      }
    );
    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  // 스키마 검증 useEffect 수정 - 불필요한 검증 제거
  // useEffect(() => {
  //   const validateForm = async () => {
  //     try {
  //       const currentValues = getValues();
  //       const valid = await schema.validate(currentValues, {
  //         abortEarly: false,
  //       });
  //       console.log("valid", valid);
  //     } catch (err) {
  //       console.log("err", err);
  //     }
  //   };

  //   validateForm();
  // }, [schema, getValues]);
  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="form">
        <div className={styles.AskPromotion}>
          <div className={styles.AskPromotionLeft}>
            <FormContainer onSubmit={handleSubmit(onSubmit)}>
              <h4 className={styles.formTitle}>
                {
                  t("siteType.free.detail.title") // 사용자 정보
                }
              </h4>
              <FormInput
                label={t("siteType.free.detail.username")}
                name="userName"
                register={register}
                errors={errors}
                placeholder={t("siteType.free.detail.enterUserName")}
                required={true}
              />
              <FormInput
                label={t("siteType.free.detail.phoneNumber")}
                name="telNumber"
                register={register}
                errors={errors}
                placeholder={t("siteType.free.detail.enterPhoneNumber")}
                // value={watch("telNumber")}
                required={true}
              />
              <FormInput
                register={register}
                name="companyName"
                label={t("siteType.free.detail.company")}
                placeholder={t("siteType.free.detail.enterCompany")}
                required={true}
                errors={errors}
              />
              <FormInput
                register={register}
                name="purpose"
                label={t("siteType.premium.detail.partnerInfo.purpose")}
                placeholder={t(
                  "siteType.premium.detail.partnerInfo.enterPurpose"
                )}
                errors={errors}
                type="textarea"
                height={80}
              />
              <h4 className={styles.formTitle}>
                {/* 요금제 세부 정보 */}
                {t("siteType.premium.detail.plan.title")}
              </h4>
              {/* {isDspProduct && (
                <FormSelect
                  label="Package"
                  name="packageName"
                  control={control}
                  errors={errors}
                  defaultValue={getValues("packageName")}
                  options={packageNameOptions}
                />
              )} */}
              {!isDspmProduct && (
                <FormInput
                  label={t("siteType.basic.detail.plan.userNum")}
                  name="licenseCount"
                  register={register}
                  errors={errors}
                  type="number"
                  width={200}
                  value={watch("licenseCount")}
                  min={10}
                  max={9999}
                  isClearBtn={false}
                />
              )}
              {isWrapsodyEco && (
                <FormInput
                  label={t("siteType.premium.detail.plan.userExtenalInvite")}
                  name="externalLicenseCount"
                  register={register}
                  errors={errors}
                  type="number"
                  width={200}
                  value={watch("externalLicenseCount")}
                  isClearBtn={false}
                  // min={10}
                  // max={9999}
                />
              )}
              {(isWrapsody || isWrapsodyEco || isFiresideProduct) && (
                <FormInput
                  register={register}
                  name="userPerStorage"
                  type="number"
                  // required={true}
                  min={1}
                  label={t("admin.couponMgnt.issued.storage")}
                  errors={errors}
                  width={200}
                  value={watch("licenseCount")}
                  isClearBtn={false}
                />
              )}
              {/* <InputField
            register={register}
            name="licenseCount"
            type="number"
            required={true}
            min={10}
            label={t("siteType.basic.detail.plan.userNum")}
            addClassName="width max-w-110"
            errors={errors}
          /> */}
              {isDspmProduct && (
                <>
                  <div className={styles.dateRowTitle}>{t("보유서버")}</div>
                  <SelectDspmInfoList />
                  <div className={styles.dateRowTitle}>{t("SaaS 앱")}</div>
                  <SelectDspmApplicationInfoList />
                </>
              )}

              <div className={styles.dateRowTitle}>
                {t("siteType.premium.detail.plan.period")}
              </div>

              <div className={styles.dateRow}>
                <FormDate
                  name="startDate"
                  label={t("siteType.premium.detail.plan.startDate")}
                  register={register}
                  control={control} // control 추가
                  errors={errors}
                  placeholder={t("siteType.premium.detail.plan.startDate")}
                  dateFormat="yyyy-MM-dd"
                  // defaultValue는 useForm의 defaultValues에서 처리하므로 제거
                />
                <FormDate
                  name="endDate"
                  label={t("siteType.premium.detail.plan.endDate")}
                  register={register}
                  control={control} // control 추가
                  errors={errors}
                  placeholder={t("siteType.premium.detail.plan.endDate")}
                  dateFormat="yyyy-MM-dd"
                  // defaultValue는 useForm의 defaultValues에서 처리하므로 제거
                />
              </div>
              <span style={{ marginTop: "24px" }}>
                <Checkbox
                  name="confirmPolicy"
                  register={register}
                  errors={errors}
                >
                  <label
                    className="checkboxLabel"
                    // style={{ display: "flex", alignItems: "center" }}
                    onClick={(e) => {
                      (setValue as any)(
                        "confirmPolicy",
                        !getValues("confirmPolicy")
                      );
                    }}
                  >
                    {t("siteType.agreement1")}
                    <a
                      onClick={(e) => {
                        e.stopPropagation();
                        openTermsService();
                      }}
                      className="cursor-pointer"
                    >
                      <b className="text text--black">
                        {t("siteType.agreement2")}
                      </b>
                    </a>
                    {t("siteType.agreement3")}
                    <a
                      onClick={(e) => {
                        e.stopPropagation();
                        openPrivatePolicy();
                      }}
                      className="cursor-pointer"
                    >
                      <b className="text text--black">
                        {t("siteType.agreement4")}
                      </b>
                    </a>
                    {/* 
                    // 유료이용약관 (지금은 사용 X)
                    {window.sessionStorage.getItem("product") ===
                      "WRAPSODY_ECO" && (
                      <>
                        {t("siteType.agreement5")}
                        <a
                          onClick={openPaidTermsService}
                          className="cursor-pointer"
                        >
                          <b className="text text--black">
                            {t("siteType.agreement6")}
                          </b>
                        </a>
                      </>
                    )} */}
                    {t("siteType.agreement7")}
                  </label>
                </Checkbox>
                <div className={styles.infoText}>{t("siteType.info")}</div>
              </span>
            </FormContainer>
          </div>
          <div className={styles.AskPromotionRight}>
            <RightDetailBox
              toggleAskPromotionOn={true}
              isCheckAskPromotion={isValid}
              setValue={setValue}
            />
          </div>
        </div>
        {/* <button
        type="submit"
        className="bg-green-500 text-white px-4 py-2 rounded"
      >
        제출
      </button> */}
      </form>
    </FormProvider>
  );
};

export default AskPromotion;
